namespace $.$$ {

	export class $shm_tests_player extends $.$shm_tests_player {

		data() {
			return this.result() as $shm_tests_app_result | null
		}

		steps() {
			return this.data()?.steps ?? []
		}

		segmented() {
			return this.data()?.media?.video_mode === 'segments' || this.steps().some( step => Boolean( step.video ) )
		}

		video() {
			return this.data()?.artifacts.find( artifact => (
				artifact.mime.startsWith( 'video/' ) && !artifact.path.includes( '/steps/' )
			) ) ?? null
		}

		step_video( index: number ) {
			return this.steps()[ index ]?.video ?? ''
		}

		video_uri() {
			const path = this.segmented() ? this.step_video( this.segment_index() ) : this.video()?.path
			return path ? this.resolve_artifact( path ) : ''
		}

		video_loop() {
			return this.segmented() && this.play_step_index() >= 0
		}

		video_autoplay() {
			return this.segmented() && this.play_step_index() >= 0
		}

		video_origin_ms() {
			const explicit = this.data()?.media?.video_started_offset_ms
			if( explicit !== null && explicit !== undefined ) return explicit
			return this.steps()[ 0 ]?.started_offset_ms ?? 0
		}

		step_start( index: number ) {
			const step = this.steps()[ index ]
			if( !step ) return 0
			return Math.max( 0, ( step.started_offset_ms - this.video_origin_ms() ) / 1000 )
		}

		step_end( index: number ) {
			const next = this.steps()[ index + 1 ]
			if( next ) return this.step_start( index + 1 )
			const step = this.steps()[ index ]
			return step ? this.step_start( index ) + step.duration_ms / 1000 : 0
		}

		step_clip_start( index: number ) {
			return Math.max( 0, this.step_start( index ) - 0.25 )
		}

		step_clip_end( index: number ) {
			return this.step_end( index ) + 0.25
		}

		current_index() {
			const steps = this.steps()
			if( !steps.length ) return -1
			if( this.segmented() ) return Math.min( this.segment_index(), steps.length - 1 )
			if( this.play_step_index() >= 0 ) return Math.min( this.play_step_index(), steps.length - 1 )
			const time = this.time()
			for( let index = steps.length - 1; index >= 0; --index ) {
				if( time >= this.step_start( index ) ) return index
			}
			return -1
		}

		current_step() {
			return this.steps()[ this.current_index() ] ?? null
		}

		override current_step_index() {
			return this.current_index()
		}

		override current_title() {
			const step = this.current_step()
			if( !step ) return this.steps().length ? 'Подготовка сценария' : 'Шаги не записаны'
			return `Шаг ${ this.current_index() + 1 } из ${ this.steps().length }: ${ step.title }`
		}

		override current_step_range() {
			const index = this.current_index()
			if( index < 0 ) return this.steps().length ? `0:00 — ${ this.clock( this.step_start( 0 ) ) }` : ''
			return `${ this.clock( this.step_start( index ) ) } — ${ this.clock( this.step_end( index ) ) }`
		}

		current_step_title() {
			return this.current_step()?.title ?? ''
		}

		current_benchmark_content() {
			return this.Current_benchmark_table().has_benchmarks()
				? [ this.Current_benchmark_table() ]
				: []
		}

		current_screenshots() {
			const paths = this.current_step()?.artifacts ?? []
			const visual = new Set( this.current_visuals().flatMap( item => [ item.actual, item.expected, item.diff ].filter( Boolean ) ) )
			return paths.filter( path => /\.(?:png|jpe?g|webp)$/i.test( path ) && !visual.has( path ) )
		}

		workspace_rows() {
			return this.video_uri() ? [ this.Video(), this.Current() ] : [ this.Current() ]
		}

		current_visuals() {
			return this.current_step()?.visual ?? []
		}

		override current_visual( id: string ) {
			return this.current_visuals()[ Number( id ) ] ?? null
		}

		current_screenshot_uri( path: string ) {
			return this.resolve_artifact( path )
		}

		current_screenshot_title( path: string ) {
			return path.split( /[\\/]/ ).at( -1 ) ?? path
		}

		current_media_rows() {
			const screenshots = this.current_screenshots()
			const visual = this.current_visuals().map( ( _, index )=> this.Current_visual( String( index ) ) )
			const regular = screenshots.map( path => this.Current_screenshot( path ) )
			return visual.length || regular.length ? [ ... visual, ... regular ] : [ this.Current_screenshot_empty() ]
		}

		play_title() {
			return this.playing() && this.play_step_index() < 0 ? 'Пауза' : 'Всё видео'
		}

		@ $mol_action
		override play_toggle( next?: Event ) {
			if( next === undefined ) return null
			const pause = this.playing() && this.play_step_index() < 0
			this.play_step_index( -1 )
			if( this.segmented() ) {
				const video = this.Video().dom_node() as HTMLVideoElement
				video.loop = false
			}
			this.playing( !pause )
			return next
		}

		play_step_enabled() {
			return this.steps().length > 0
		}

		play_step_active( next?: boolean ) {
			return this.playing() && this.play_step_index() >= 0
		}

		play_step_title() {
			return this.play_step_active() ? 'Остановить цикл' : 'Зациклить шаг'
		}

		@ $mol_action
		override play_step( next?: Event ) {
			if( next === undefined || !this.play_step_enabled() ) return next ?? null
			if( this.playing() && this.play_step_index() >= 0 ) {
				this.play_step_index( -1 )
				this.playing( false )
				return next
			}
			const index = Math.max( 0, this.current_index() )
			this.loop_step( index )
			return next
		}

		@ $mol_action
		loop_step( index: number ) {
			if( this.segmented() ) {
				this.show_segment( index, true, true )
				return
			}
			this.play_step_index( index )
			this.time( this.step_clip_start( index ) + 0.001 )
			this.playing( true )
		}

		@ $mol_action
		override step_timeupdate( next?: Event ) {
			this.Video().retime( next )
			if( this.segmented() ) return next ?? null
			const index = this.play_step_index()
			if( index < 0 ) return next ?? null
			const end = this.step_clip_end( index )
			const video = this.Video().dom_node() as HTMLVideoElement
			if( video.currentTime < end ) return next ?? null
			this.Video().time( this.step_clip_start( index ) + 0.001 )
			if( !this.Video().playing() ) this.Video().playing( true )
			return next ?? null
		}

		@ $mol_action
		show_segment( index: number, play: boolean, loop: boolean ) {
			const uri = this.step_video( index )
			if( !uri ) return
			this.segment_index( index )
			this.play_step_index( loop ? index : -1 )
			const video = this.Video().dom_node() as HTMLVideoElement
			video.autoplay = play
			video.muted = true
			video.src = this.resolve_artifact( uri )
			video.loop = loop
			video.load()
			if( play ) {
				video.play().catch( error => {
					if( error?.name !== 'AbortError' ) console.warn( 'Video autoplay failed', error )
				} )
			} else {
				video.pause()
			}
		}

		@ $mol_action
		override video_ended( next?: Event ) {
			if( !this.segmented() || this.play_step_index() >= 0 ) return next ?? null
			const index = this.segment_index() + 1
			if( index < this.steps().length ) this.show_segment( index, true, false )
			return next ?? null
		}

		speed_title() {
			return `${ this.speed() }×`
		}

		@ $mol_action
		override speed_cycle( next?: Event ) {
			if( next === undefined ) return null
			const speeds = [ 0.5, 1, 1.5, 2 ]
			const index = speeds.indexOf( this.speed() )
			const speed = speeds[ ( index + 1 ) % speeds.length ]
			this.speed( speed )
			const video = this.Video().dom_node() as HTMLVideoElement
			video.playbackRate = speed
			return next
		}

		time_title() {
			return `${ this.clock( this.time() ) } / ${ this.clock( this.media_duration() ) }`
		}

		clock( seconds: number ) {
			if( !Number.isFinite( seconds ) ) return '0:00'
			const rounded = Math.max( 0, Math.floor( seconds ) )
			return `${ Math.floor( rounded / 60 ) }:${ String( rounded % 60 ).padStart( 2, '0' ) }`
		}

		duration( milliseconds: number ) {
			return milliseconds < 1000 ? `${ milliseconds } мс` : `${ ( milliseconds / 1000 ).toFixed( 1 ) } с`
		}

		status_title( status: string ) {
			switch( status ) {
				case 'passed': return 'пройден'
				case 'failed': return 'ошибка'
				default: return status
			}
		}

		resolve_artifact( path: string ) {
			return new URL( path.replace( /\\/g, '/' ), new URL( this.manifest_uri(), this.$.$mol_dom_context.location.href ) ).toString()
		}

	}

}
